/**
 * EV Pricing Engine tabs — mirrors the ICE Reference tab structure but with
 * EV-specific fields (subsidy, accessory bundle, insurance rates for scooter
 * class, zero-dep, hypothecation, 5% GST split 2.5/2.5) and 16 models seeded
 * from EV_PRICE_LIST_MASTER_V1.xlsx.
 *
 * All API calls hit /api/ev-pricing/*.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Calculator, RocketLaunch, MagnifyingGlass, UploadSimple } from "@phosphor-icons/react";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const EV_REF_EMPTY = {
  package_name: "", branch_type_code: "GENERAL", model_category: "ELECTRIC", active: true,
  unique_code: "",
  unit_cost: 0, dealer_margin: 0, hsrp_amount: 350, subsidy: 5000,
  software_upgrade: 0, reg_charges: 1400, fac: 1000, tr_charges: 0,
  offboard_charger: 0, number_plate_bracket: 90, hsrp_plates: 350,
  pillion_footrest: 875, all_round_guard: 3200, centre_stand: 986, floor_mat: 415,
  helmet_sale_price: 0,
  insurance_wo_nildip: 0, idv_pct: 95, od_rate_pct: 1.676, tp_premium: 3851,
  pa: 525, nildip_pct: 0.4, insurance_gst_pct: 18,
  zero_dep_addon: 750, hypothecation: 500,
  amc_4y: 2500, extended_warranty: 8000, cash_back_offer: 0, card_based_offer: 0,
  ad_margin_discount: 0, ad_margin_discount_pct: 0,
  ad_number_plate_bracket: 47, ad_pillion_footrest: 538,
  ad_all_round_guard: 1155, ad_centre_stand: 545, ad_helmet: 641,
  company_discount: 0,
  package_hsn: "87116020", helmet_hsn: "87116020", accessories_hsn: "87116020",
  package_igst_pct: 2.5, package_cgst_pct: 2.5,
  helmet_igst_pct: 9, helmet_cgst_pct: 9,
  accessories_igst_pct: 9, accessories_cgst_pct: 9,
};

/* =============================================================
 * EV REFERENCE tab (list + edit + bulk)
 * ============================================================= */
export function EvReferenceTab() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [editing, setEditing] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/ev-pricing/references", { params: { status: statusFilter } });
      setRows(data || []);
      setEdits({});
    } catch (e) { toast.error(formatApiError(e)); }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const s = search.trim().toLowerCase();
    return s ? rows.filter((r) => r.package_name.toLowerCase().includes(s)) : rows;
  }, [rows, search]);

  const softDelete = async (r) => {
    if (!window.confirm(`Move EV reference "${r.package_name}" to Deleted bin?`)) return;
    try { await api.delete(`/ev-pricing/references/${r.id}`); toast.success("Moved to Deleted bin"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const restore = async (r) => {
    try { await api.post(`/ev-pricing/references/${r.id}/restore`); toast.success("Restored"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const purge = async (r) => {
    if (!window.confirm(`⚠️ PERMANENTLY delete "${r.package_name}"?`)) return;
    if (!window.confirm("Are you absolutely sure? Last chance.")) return;
    try { await api.delete(`/ev-pricing/references/${r.id}/purge`); toast.success("Permanently deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const setCell = (rid, field, value) => setEdits((e) => ({ ...e, [rid]: { ...(e[rid] || {}), [field]: value } }));
  const dirtyCount = Object.keys(edits).filter((rid) =>
    Object.values(edits[rid] || {}).some((v) => v !== "" && v !== undefined)
  ).length;
  const saveBulk = async () => {
    const updates = Object.entries(edits).map(([id, patch]) => {
      const clean = { id };
      for (const [k, v] of Object.entries(patch)) {
        if (v !== "" && v !== undefined && !Number.isNaN(Number(v))) clean[k] = Number(v);
      }
      return Object.keys(clean).length > 1 ? clean : null;
    }).filter(Boolean);
    if (!updates.length) { toast.error("No changes to save"); return; }
    if (!window.confirm(`Save changes to ${updates.length} row(s)?`)) return;
    setSaving(true);
    try {
      const { data } = await api.post("/ev-pricing/references/bulk-update", { updates });
      toast.success(`Saved ${data.changed} row(s)`);
      setBulkMode(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search EV model…" data-testid="ev-ref-search"
            className="w-full pl-8 pr-3 py-2 bg-canvas border border-canvas-line text-sm" />
        </div>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setBulkMode(false); setEdits({}); }}
          data-testid="ev-ref-status"
          className={`px-3 py-2 border text-sm ${statusFilter === "deleted" ? "border-rose-400 bg-rose-50 text-rose-800" : "bg-canvas border-canvas-line"}`}>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="all">All</option>
          <option value="deleted">🗑 Deleted bin</option>
        </select>
        <button
          onClick={async () => {
            try {
              const resp = await api.get("/ev-pricing/references/export", { responseType: "blob" });
              const url = URL.createObjectURL(new Blob([resp.data]));
              const a = document.createElement("a");
              a.href = url;
              a.download = `ev-pricing-master-${new Date().toISOString().slice(0, 10)}.xlsx`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
              toast.success("Exported EV pricing master workbook");
            } catch (e) { toast.error(formatApiError(e)); }
          }}
          data-testid="ev-ref-export"
          className="px-3 py-2 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:border-ink"
        >
          Export XLSX
        </button>
        <label
          className="px-3 py-2 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:border-ink cursor-pointer flex items-center gap-1"
          data-testid="ev-ref-import-btn"
        >
          <UploadSimple size={12} weight="bold" />
          Import XLSX
          <input type="file" accept=".xlsx,.xlsm,.csv" className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (!f) return;
              try {
                const buf = await f.arrayBuffer();
                let binary = "";
                const bytes = new Uint8Array(buf);
                for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
                const file_b64 = btoa(binary);
                const prev = await api.post("/ev-pricing/references/import", { file_b64, filename: f.name, dry_run: true });
                const p = prev.data;
                const willUpdate = (p.preview || []).filter((x) => x.status === "will_update").length;
                const unchanged = (p.preview || []).filter((x) => x.status === "unchanged").length;
                if (!window.confirm(
                  `Rows read: ${p.total_rows}\nWill update: ${willUpdate}\nUnchanged: ${unchanged}\nMissing (not found): ${p.missing}\n\nProceed with import?`
                )) return;
                const result = await api.post("/ev-pricing/references/import", { file_b64, filename: f.name, dry_run: false });
                toast.success(`Imported ${result.data.updated} row(s)`);
                load();
              } catch (err) { toast.error(formatApiError(err)); }
            }} />
        </label>
        <button onClick={() => { setBulkMode((m) => !m); setEdits({}); }}
          disabled={statusFilter === "deleted"}
          data-testid="ev-ref-bulk-toggle"
          className={`px-3 py-2 text-xs font-mono uppercase tracking-wider border ${bulkMode ? "bg-amber-600 text-white border-amber-600" : "border-canvas-line hover:border-ink"} disabled:opacity-40`}>
          {bulkMode ? "Exit Bulk Edit" : "Bulk Edit"}
        </button>
        {!bulkMode && statusFilter !== "deleted" && (
          <button onClick={() => setEditing({ ...EV_REF_EMPTY })} data-testid="ev-ref-add"
            className="px-3 py-2 bg-emerald-700 text-white text-xs font-mono uppercase tracking-wider hover:bg-emerald-800 flex items-center gap-1">
            <Plus size={12} weight="bold" /> New EV Reference
          </button>
        )}
      </div>

      {bulkMode && dirtyCount > 0 && (
        <div className="flex items-center justify-between border border-amber-300 bg-amber-50/40 px-3 py-2">
          <div className="text-xs font-semibold text-amber-800">{dirtyCount} row(s) with unsaved edits</div>
          <div className="flex gap-2">
            <button onClick={() => setEdits({})} className="px-3 py-1 border border-canvas-line text-xs font-mono uppercase">Discard</button>
            <button onClick={saveBulk} disabled={saving} data-testid="ev-ref-save-all"
              className="px-3 py-1 bg-emerald-700 text-white text-xs font-mono uppercase">
              {saving ? "Saving…" : `Save All (${dirtyCount})`}
            </button>
          </div>
        </div>
      )}

      <div className="border border-canvas-line overflow-x-auto max-h-[60vh] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-canvas-panel border-b border-canvas-line sticky top-0">
            <tr>
              {(bulkMode
                ? ["Model", "Unit Cost", "Margin", "Subsidy", "SW Upgrade", "Guard", ""]
                : ["Model", "Unit Cost", "Margin", "Subsidy", "Insurance", "Last Published", ""]
              ).map((h) => (
                <th key={h} className="px-3 py-2 font-mono text-[10px] uppercase text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-ink-muted text-xs">No EV references match.</td></tr>
            ) : visible.map((r) => {
              const isDirty = !!edits[r.id] && Object.values(edits[r.id]).some((v) => v !== "" && v !== undefined);
              return (
                <tr key={r.id}
                  className={`border-b border-canvas-line/50 ${isDirty ? "bg-amber-50/60" : ""}`}
                  data-testid={`ev-ref-row-${r.id}`}>
                  <td className="px-3 py-2 text-sm font-semibold">
                    {r.package_name}
                    {r.active === false && (
                      <span className="ml-2 px-1.5 py-0.5 bg-ink-muted/20 text-ink-muted text-[9px] font-mono uppercase">Inactive</span>
                    )}
                  </td>
                  {bulkMode ? (
                    <>
                      <BulkCell r={r} field="unit_cost" edits={edits} setCell={setCell} />
                      <BulkCell r={r} field="dealer_margin" edits={edits} setCell={setCell} />
                      <BulkCell r={r} field="subsidy" edits={edits} setCell={setCell} />
                      <BulkCell r={r} field="software_upgrade" edits={edits} setCell={setCell} />
                      <BulkCell r={r} field="all_round_guard" edits={edits} setCell={setCell} />
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-2 font-mono text-xs">{inr(r.unit_cost)}</td>
                      <td className="px-3 py-2 font-mono text-xs">{inr(r.dealer_margin)}</td>
                      <td className="px-3 py-2 font-mono text-xs">{inr(r.subsidy)}</td>
                      <td className="px-3 py-2 font-mono text-xs">{inr(r.insurance_wo_nildip)}</td>
                      <td className="px-3 py-2 font-mono text-[10px]">
                        {r.last_published_at ? (
                          <>
                            <div>{inr(r.last_published_price)}</div>
                            <div className="text-ink-muted">{new Date(r.last_published_at).toLocaleString()}</div>
                          </>
                        ) : <span className="text-ink-muted">—</span>}
                      </td>
                    </>
                  )}
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {statusFilter === "deleted" ? (
                      <>
                        <button onClick={() => restore(r)} data-testid={`ev-ref-restore-${r.id}`}
                          className="text-emerald-700 hover:bg-emerald-50 p-1 text-xs font-mono uppercase">Restore</button>
                        <button onClick={() => purge(r)} data-testid={`ev-ref-purge-${r.id}`}
                          className="text-rose-700 hover:bg-rose-50 p-1 text-xs font-mono uppercase">Purge</button>
                      </>
                    ) : !bulkMode && (
                      <>
                        <button onClick={() => setEditing(r)} className="text-ink-muted hover:text-ink p-1" title="Edit"><PencilSimple size={12} /></button>
                        <button onClick={() => softDelete(r)} className="text-rose-700 hover:bg-rose-50 p-1" title="Delete"><Trash size={12} /></button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && <EvReferenceEditor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function BulkCell({ r, field, edits, setCell }) {
  const val = edits[r.id]?.[field];
  const hasEdit = val !== undefined && val !== "";
  return (
    <td className="px-2 py-1">
      <input type="number" step="0.01"
        value={hasEdit ? val : (r[field] ?? 0)}
        onChange={(e) => setCell(r.id, field, e.target.value)}
        data-testid={`ev-bulk-cell-${r.id}-${field}`}
        className={`w-full px-2 py-1 text-xs font-mono border ${hasEdit ? "border-amber-500 bg-amber-50" : "border-canvas-line bg-canvas"}`} />
    </td>
  );
}

function EvReferenceEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const isNew = !initial.id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const computePreview = async () => {
    try {
      const { data } = await api.post("/ev-pricing/compute", { reference: form });
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    if (!form.package_name.trim()) { toast.error("Package name is required"); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/ev-pricing/references", form);
        toast.success("EV reference created");
      } else {
        await api.put(`/ev-pricing/references/${initial.id}`, form);
        toast.success("EV reference updated");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-4" data-testid="ev-ref-editor">
      <div className="bg-canvas border border-canvas-line max-w-5xl w-full max-h-[92vh] overflow-y-auto">
        <div className="border-b border-canvas-line px-6 py-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">EV Package Reference</div>
            <div className="text-lg font-semibold">{isNew ? "New EV Reference" : form.package_name}</div>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink text-xl">×</button>
        </div>

        <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-3 text-sm">
          <Field label="Package name" required value={form.package_name} onChange={(v) => set("package_name", v)} testId="ev-ref-name" />
          <SelectField label="Branch type" value={form.branch_type_code} onChange={(v) => set("branch_type_code", v)} options={["GENERAL", "AD"]} />
          <label className="flex items-center gap-2 text-xs mt-6">
            <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} /> Active
          </label>

          <SectionTitle title="Vehicle Cost (TVS)" />
          <Num label="Unit cost (TVS)" value={form.unit_cost} onChange={(v) => set("unit_cost", v)} testId="ev-ref-unit-cost" />
          <Num label="Dealer margin ₹" value={form.dealer_margin} onChange={(v) => set("dealer_margin", v)} testId="ev-ref-margin" />
          <Num label="Subsidy ₹ (state/central)" value={form.subsidy} onChange={(v) => set("subsidy", v)} testId="ev-ref-subsidy" />
          <Num label="HSRP amount" value={form.hsrp_amount} onChange={(v) => set("hsrp_amount", v)} />

          <SectionTitle title="Registration + One-Time Components" />
          <Num label="Software upgrade" value={form.software_upgrade} onChange={(v) => set("software_upgrade", v)} />
          <Num label="Registration" value={form.reg_charges} onChange={(v) => set("reg_charges", v)} />
          <Num label="FAC" value={form.fac} onChange={(v) => set("fac", v)} />
          <Num label="TR charges" value={form.tr_charges} onChange={(v) => set("tr_charges", v)} />

          <SectionTitle title="EV Accessory Bundle (retail, incl. GST)" />
          <Num label="Offboard charger" value={form.offboard_charger} onChange={(v) => set("offboard_charger", v)} />
          <Num label="Number plate bracket" value={form.number_plate_bracket} onChange={(v) => set("number_plate_bracket", v)} />
          <Num label="HSRP plates" value={form.hsrp_plates} onChange={(v) => set("hsrp_plates", v)} />
          <Num label="Pillion footrest" value={form.pillion_footrest} onChange={(v) => set("pillion_footrest", v)} />
          <Num label="All-round guard" value={form.all_round_guard} onChange={(v) => set("all_round_guard", v)} />
          <Num label="Centre stand" value={form.centre_stand} onChange={(v) => set("centre_stand", v)} />
          <Num label="Floor mat" value={form.floor_mat} onChange={(v) => set("floor_mat", v)} />
          <Num label="Helmet sale price" value={form.helmet_sale_price} onChange={(v) => set("helmet_sale_price", v)} />

          <SectionTitle title="Insurance — ICE Scooter formula (auto-derived) + Zero-Dep" />
          <Num label="Insurance w/o Nil-dip (static override)" value={form.insurance_wo_nildip} onChange={(v) => set("insurance_wo_nildip", v)} />
          <Num label="Zero-dep add-on" value={form.zero_dep_addon} onChange={(v) => set("zero_dep_addon", v)} />
          <Num label="Hypothecation" value={form.hypothecation} onChange={(v) => set("hypothecation", v)} />
          <Num label="IDV % of ex-showroom" value={form.idv_pct} onChange={(v) => set("idv_pct", v)} step="0.0001" />
          <Num label="OD % of IDV" value={form.od_rate_pct} onChange={(v) => set("od_rate_pct", v)} step="0.0001" />
          <Num label="TP premium ₹" value={form.tp_premium} onChange={(v) => set("tp_premium", v)} />
          <Num label="PA cover ₹" value={form.pa} onChange={(v) => set("pa", v)} />
          <Num label="Nil-dip % of IDV" value={form.nildip_pct} onChange={(v) => set("nildip_pct", v)} step="0.001" />
          <Num label="Insurance GST %" value={form.insurance_gst_pct} onChange={(v) => set("insurance_gst_pct", v)} step="0.1" />
          <div className="lg:col-span-3 text-[10px] text-ink-muted font-mono">
            Insurance derives from ex-showroom via the ICE-scooter formula
            family (IDV × 95%, OD × rate, TP flat, PA, + 18% GST). The static
            override above wins if set — mirrors the sheet's fixed ₹6,450.
          </div>

          <SectionTitle title="Quote extras (used by future quote flow)" />
          <Num label="AMC (4 years)" value={form.amc_4y} onChange={(v) => set("amc_4y", v)} />
          <Num label="Extended warranty" value={form.extended_warranty} onChange={(v) => set("extended_warranty", v)} />
          <Num label="Cash-back offer" value={form.cash_back_offer} onChange={(v) => set("cash_back_offer", v)} />
          <Num label="Card-based offer" value={form.card_based_offer} onChange={(v) => set("card_based_offer", v)} />

          <SectionTitle title="AD (Sub-Dealer) — MASTER_AD_EV" />
          <Num label="AD margin discount ₹ (or leave 0 to auto-derive)" value={form.ad_margin_discount} onChange={(v) => set("ad_margin_discount", v)} />
          <Num label="AD margin discount % (optional)" value={form.ad_margin_discount_pct} onChange={(v) => set("ad_margin_discount_pct", v)} step="0.01" />
          <Num label="AD bracket landing" value={form.ad_number_plate_bracket} onChange={(v) => set("ad_number_plate_bracket", v)} />
          <Num label="AD pillion footrest" value={form.ad_pillion_footrest} onChange={(v) => set("ad_pillion_footrest", v)} />
          <Num label="AD all-round guard" value={form.ad_all_round_guard} onChange={(v) => set("ad_all_round_guard", v)} />
          <Num label="AD centre stand" value={form.ad_centre_stand} onChange={(v) => set("ad_centre_stand", v)} />
          <Num label="AD helmet" value={form.ad_helmet} onChange={(v) => set("ad_helmet", v)} />

          <SectionTitle title="GST + HSN (EV: package 2.5+2.5)" />
          <Field label="Package HSN" value={form.package_hsn} onChange={(v) => set("package_hsn", v)} />
          <Field label="Helmet HSN" value={form.helmet_hsn} onChange={(v) => set("helmet_hsn", v)} />
          <Field label="Accessories HSN" value={form.accessories_hsn} onChange={(v) => set("accessories_hsn", v)} />
          <Num label="Package IGST %" value={form.package_igst_pct} onChange={(v) => set("package_igst_pct", v)} step="0.01" />
          <Num label="Package CGST %" value={form.package_cgst_pct} onChange={(v) => set("package_cgst_pct", v)} step="0.01" />
          <Num label="Company discount" value={form.company_discount} onChange={(v) => set("company_discount", v)} />
        </div>

        {preview && (
          <div className="mx-6 mb-4 border border-emerald-300 bg-emerald-50/40 p-4 text-xs" data-testid="ev-ref-preview">
            <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-2">Live Preview</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="font-semibold mb-1">EV Retail (GENERAL)</div>
                {Object.entries(preview.retail).map(([k, v]) => (
                  <div key={k} className="flex justify-between font-mono"><span className="text-ink-muted">{k}</span><span>{inr(v)}</span></div>
                ))}
              </div>
              <div>
                <div className="font-semibold mb-1">EV AD (Sub-Dealer)</div>
                {Object.entries(preview.ad).map(([k, v]) => (
                  <div key={k} className="flex justify-between font-mono"><span className="text-ink-muted">{k}</span><span>{inr(v)}</span></div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="border-t border-canvas-line px-6 py-3 flex justify-between">
          <button onClick={computePreview} data-testid="ev-ref-preview-btn"
            className="px-4 py-2 border border-canvas-line text-sm hover:border-ink flex items-center gap-1">
            <Calculator size={12} weight="bold" /> Compute Preview
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-canvas-line text-sm hover:border-ink">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="ev-ref-save"
              className="px-4 py-2 bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800 disabled:opacity-50">
              {saving ? "Saving…" : (isNew ? "Create EV Reference" : "Save Changes")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


/* =============================================================
 * EV MASTER tab (compute + publish, GENERAL or AD)
 * ============================================================= */
export function EvMasterTab({ branchTypeCode }) {
  const [rows, setRows] = useState([]);
  const [computed, setComputed] = useState({});
  const [publishBusy, setPublishBusy] = useState({});
  const isAd = branchTypeCode === "AD";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/ev-pricing/references", { params: { status: "all" } });
      const refs = (data || []).filter((r) => !r.deleted_at);
      setRows(refs);
      const computes = await Promise.all(
        refs.map(async (ref) => {
          try {
            const { data: c } = await api.post("/ev-pricing/compute", { reference: ref });
            return [ref.id, c];
          } catch { return [ref.id, null]; }
        })
      );
      setComputed(Object.fromEntries(computes));
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const publish = async (r) => {
    if (!window.confirm(`Publish EV "${r.package_name}" to packages master (${branchTypeCode})?`)) return;
    setPublishBusy((b) => ({ ...b, [r.id]: true }));
    try {
      const { data } = await api.post(`/ev-pricing/references/${r.id}/publish`, null, { params: { target: branchTypeCode } });
      toast.success(`${data.action === "created" ? "Created" : "Updated"} — ${inr(data.price)}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPublishBusy((b) => ({ ...b, [r.id]: false })); }
  };

  const publishAll = async () => {
    if (!window.confirm(`Publish ALL ${rows.length} EV references to packages (${branchTypeCode})?`)) return;
    let ok = 0, fail = 0;
    for (const r of rows) {
      try {
        await api.post(`/ev-pricing/references/${r.id}/publish`, null, { params: { target: branchTypeCode } });
        ok++;
      } catch (e) { fail++; toast.error(`${r.package_name}: ${formatApiError(e)}`); }
    }
    toast.success(`Published ${ok} EV packages${fail ? ` (${fail} failed)` : ""}`);
    load();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-ink-muted">
          {rows.length} EV reference(s). {isAd ? "AD" : "Retail"} on-road computed live — click Publish to push into <code>packages</code>.
        </div>
        {rows.length > 0 && (
          <button onClick={publishAll} data-testid="ev-master-publish-all"
            className="px-3 py-2 bg-emerald-700 text-white text-xs font-mono uppercase tracking-wider hover:bg-emerald-800 flex items-center gap-1">
            <RocketLaunch size={12} weight="bold" /> Publish All
          </button>
        )}
      </div>

      <div className="border border-canvas-line overflow-x-auto max-h-[65vh] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-canvas-panel border-b border-canvas-line sticky top-0">
            <tr>
              <th className="px-3 py-2 font-mono text-[10px] uppercase text-left">Model</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase text-left">Unit Cost</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase text-left">Subsidy</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase text-left">Ex-Show</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase text-left">{isAd ? "AD Total" : "Insurance"}</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase text-left font-semibold">{isAd ? "AD Price ₹" : "On-Road ₹"}</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase text-left">Published</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-ink-muted text-xs">No EV references yet.</td></tr>
            ) : rows.map((r) => {
              const c = computed[r.id];
              const rt = c?.retail || {};
              const ad = c?.ad || {};
              const onroad = isAd ? ad.ad_total : rt.on_road;
              return (
                <tr key={r.id} className="border-b border-canvas-line/50" data-testid={`ev-master-row-${r.id}`}>
                  <td className="px-3 py-2 text-sm font-semibold">{r.package_name}</td>
                  <td className="px-3 py-2 font-mono text-xs">{inr(r.unit_cost)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{inr(r.subsidy)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{inr(isAd ? ad.ex_showroom : rt.ex_showroom)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{isAd ? inr(ad.ad_accessories_inc_gst) : inr(rt.insurance_wo_nildip)}</td>
                  <td className="px-3 py-2 font-mono text-sm font-semibold text-emerald-700">{inr(onroad)}</td>
                  <td className="px-3 py-2 font-mono text-[10px]">
                    {r.last_published_at ? (
                      <>
                        <div>{inr(r.last_published_price)}</div>
                        <div className="text-ink-muted">{new Date(r.last_published_at).toLocaleDateString()}</div>
                      </>
                    ) : <span className="text-ink-muted">Never</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => publish(r)} disabled={publishBusy[r.id]}
                      data-testid={`ev-master-publish-${r.id}`}
                      className="px-2 py-1 border border-emerald-700 text-emerald-700 text-[10px] font-mono uppercase tracking-wider hover:bg-emerald-50 disabled:opacity-50">
                      {publishBusy[r.id] ? "…" : "Publish"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ---------- shared field helpers ---------- */
function Field({ label, value, onChange, testId, required }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1">{label}{required ? " *" : ""}</label>
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
    </div>
  );
}
function Num({ label, value, onChange, testId, step = "0.01" }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1">{label}</label>
      <input type="number" step={step} value={value ?? 0}
        onChange={(e) => onChange(e.target.value === "" ? 0 : parseFloat(e.target.value))}
        data-testid={testId}
        className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm tabular-nums font-mono" />
    </div>
  );
}
function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
function SectionTitle({ title }) {
  return (
    <div className="lg:col-span-3 pt-3 pb-1 border-b border-canvas-line font-mono text-[10px] uppercase tracking-widest text-ink-muted">
      {title}
    </div>
  );
}
